import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { 
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  Database,
  UserCheck,
  Mail,
  Calendar,
  Globe,
  Server,
  FileText,
} from 'lucide-react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import chronoLogo from '../images/Chrono Session - removed bg logo.png'

const PrivacyPolicyPage = () => {
  const lastUpdated = "December 2024"

  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: Database,
      content: [
        {
          subtitle: 'Personal Information',
          items: [
            'Email addresses when you create an account or contact us',
            'Name and contact details when provided voluntarily',
            'Communication preferences and support inquiries'
          ]
        },
        {
          subtitle: 'Usage Data',
          items: [
            'Browser session metadata (tab titles, URLs, timestamps)',
            'Extension usage statistics and feature interactions',
            'Device information and browser type for compatibility',
            'IP addresses for security and fraud prevention'
          ]
        },
        {
          subtitle: 'Cookies and Tracking',
          items: [
            'Essential cookies for authentication and security',
            'Analytics cookies to improve our service (with your consent)',
            'Local storage for offline functionality and user preferences'
          ]
        }
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: UserCheck,
      content: [
        {
          subtitle: 'Service Provision',
          items: [
            'Saving and syncing your browser sessions across devices',
            'Enabling session sharing and collaboration features',
            'Providing AI-powered tab organization and insights',
            'Delivering customer support and technical assistance'
          ]
        },
        {
          subtitle: 'Service Improvement',
          items: [
            'Analyzing usage patterns to enhance features',
            'Identifying and fixing bugs or performance issues',
            'Developing new features based on user behavior',
            'Conducting research to improve user experience'
          ]
        },
        {
          subtitle: 'Communication',
          items: [
            'Sending important service updates and security notifications',
            'Responding to your inquiries and support requests',
            'Sharing feature announcements (with your permission)',
            'Providing educational content about productivity features'
          ]
        }
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: Globe,
      content: [
        {
          subtitle: 'We Never Sell Your Data',
          items: [
            'Your personal information is never sold to third parties',
            'Session data remains private and encrypted',
            'We only share data in limited circumstances outlined below'
          ]
        },
        {
          subtitle: 'Limited Sharing Scenarios',
          items: [
            'With your explicit consent for specific features',
            'To comply with legal obligations or court orders',
            'To protect our rights, privacy, safety, or property',
            'In case of business transfer (with continued privacy protection)'
          ]
        },
        {
          subtitle: 'Service Providers',
          items: [
            'Trusted cloud hosting providers (with strict data protection agreements)',
            'Analytics services that help us improve the product',
            'Customer support tools to assist with your inquiries'
          ]
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security and Protection',
      icon: Lock,
      content: [
        {
          subtitle: 'Encryption and Security',
          items: [
            'All data is encrypted in transit using TLS/SSL protocols',
            'Session data is encrypted at rest using industry-standard encryption',
            'Secure authentication using OAuth 2.0 and JWT tokens',
            'Regular security audits and vulnerability assessments'
          ]
        },
        {
          subtitle: 'Access Controls',
          items: [
            'Strict employee access controls with principle of least privilege',
            'Multi-factor authentication for all administrative accounts',
            'Regular access reviews and immediate revocation upon role changes',
            'Comprehensive logging and monitoring of data access'
          ]
        },
        {
          subtitle: 'Infrastructure Security',
          items: [
            'Secure cloud infrastructure with automated security updates',
            'Network isolation and firewall protection',
            'Regular backup and disaster recovery procedures',
            'Incident response plan for potential security breaches'
          ]
        }
      ]
    },
    {
      id: 'user-rights',
      title: 'Your Rights and Controls',
      icon: Eye,
      content: [
        {
          subtitle: 'Data Access and Portability',
          items: [
            'Request a copy of all your personal data',
            'Export your session data in machine-readable formats',
            'View and manage your privacy settings through your account',
            'Access logs of how your data has been used'
          ]
        },
        {
          subtitle: 'Data Correction and Deletion',
          items: [
            'Update or correct your personal information at any time',
            'Delete specific sessions or your entire account',
            'Request removal of your data from our systems',
            'Automated data retention and deletion policies'
          ]
        },
        {
          subtitle: 'Consent Management',
          items: [
            'Withdraw consent for optional data processing',
            'Opt-out of marketing communications',
            'Control cookie and tracking preferences',
            'Manage session sharing and collaboration settings'
          ]
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention and Deletion',
      icon: Calendar,
      content: [
        {
          subtitle: 'Retention Periods',
          items: [
            'Active session data: Retained while you use the service',
            'Account data: Retained until account deletion',
            'Usage analytics: Aggregated and anonymized after 24 months',
            'Support communications: Retained for 3 years for quality assurance'
          ]
        },
        {
          subtitle: 'Automated Deletion',
          items: [
            'Inactive accounts are deleted after 2 years of inactivity',
            'Temporary data (caches, logs) is automatically purged',
            'Deleted sessions are permanently removed within 30 days',
            'Backup data is securely wiped according to retention schedules'
          ]
        }
      ]
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: Server,
      content: [
        {
          subtitle: 'Global Service Delivery',
          items: [
            'Data may be processed in countries where our service providers operate',
            'All transfers comply with applicable data protection laws',
            'Adequate protection measures are in place for international transfers',
            'Users are notified of significant changes to data processing locations'
          ]
        },
        {
          subtitle: 'Protection Measures',
          items: [
            'Standard Contractual Clauses for EU data transfers',
            'Adequacy decisions where applicable',
            'Additional safeguards for sensitive data transfers',
            'Regular compliance reviews with international partners'
          ]
        }
      ]
    },
    {
      id: 'children-privacy',
      title: 'Children\'s Privacy',
      icon: Shield,
      content: [
        {
          subtitle: 'Age Restrictions',
          items: [
            'Our service is not intended for children under 13 years of age',
            'We do not knowingly collect personal information from children',
            'If we discover we have collected data from a child, we will delete it immediately',
            'Parents can contact us to request deletion of their child\'s data'
          ]
        }
      ]
    },
    {
      id: 'policy-changes',
      title: 'Policy Changes and Updates',
      icon: FileText,
      content: [
        {
          subtitle: 'Notification Process',
          items: [
            'Users will be notified of significant privacy policy changes',
            'Changes will be posted on this page with the updated date',
            'Email notifications for material changes affecting your rights',
            'Continued use of the service constitutes acceptance of updates'
          ]
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Helmet>
        <title>Privacy Policy | Chrono Session - Your Data, Your Control</title>
        <meta name="description" content="Learn how Chrono Session protects your privacy and manages your data. Comprehensive privacy policy covering data collection, usage, security, and your rights." />
        <meta name="keywords" content="privacy policy, data protection, Chrono Session privacy, browser extension privacy, data security, user rights, GDPR compliance" />
      </Helmet>

      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-indigo-600/10" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Back Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <a 
              href="/" 
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 group"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="text-sm font-medium">Back to Home</span>
            </a>
          </motion.div>

          {/* Logo and Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <LazyLoadImage 
                src={chronoLogo} 
                alt="Chrono Session" 
                className="h-16 w-auto object-contain" 
                effect="blur"
              />
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Privacy
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent"> Policy</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Your privacy is our priority. This policy explains how we collect, use, and protect your information when you use Chrono Session.
            </p>
            
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              <span>Last updated: {lastUpdated}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="block py-2 px-3 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                    >
                      {index + 1}. {section.title}
                    </a>
                  ))}
                </nav>
              </motion.div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="space-y-12">
              {sections.map((section, sectionIndex) => (
                <motion.section
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                  className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg p-8"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                      <section.icon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  </div>

                  <div className="space-y-6">
                    {section.content.map((subsection, index) => (
                      <div key={index}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">{subsection.subtitle}</h3>
                        <ul className="space-y-2">
                          {subsection.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start space-x-3">
                              <div className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mt-2" />
                              <span className="text-gray-700 leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-gray-200/50 shadow-lg p-8 text-center"
        >
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
              <Mail className="h-8 w-8 text-white" />
            </div>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Questions About Your Privacy?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            If you have any questions about this Privacy Policy or how we handle your data, 
            please don't hesitate to contact us. We're here to help.
          </p>
          
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
          >
            <Mail className="h-5 w-5" />
            <span>Contact Us</span>
          </a>
        </motion.div>
      </div>
    </div>
  )
}

export default PrivacyPolicyPage
